"use client";

import {
  ChevronDown,
  CornerUpRight,
  Frame,
  Hand,
  LayoutPanelTop,
  MessageCircle,
  MousePointer2,
  Plus,
  Sparkles,
  Stamp,
  Table,
  Type,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";

import { useSequence } from "../use-sequence";
import { AppWindow, Avatar, Cursor } from "../visual-kit";

// =============================================================================

type Entity = {
  name: string;
  attributes: string[];
  relations: string[];
  /** Position in % of the board */
  left: number;
  top: number;
};

const ENTITIES: Entity[] = [
  {
    name: "User",
    attributes: [
      "id: String",
      "name: String",
      "email: String @unique",
      "createdAt: DateTime",
    ],
    relations: ["bookings: Booking[]"],
    left: 6,
    top: 9,
  },
  {
    name: "Booking",
    attributes: [
      "id: String",
      "date: DateTime",
      "guests: Int",
      "status: Status",
    ],
    relations: ["user: User", "restaurant: Restaurant"],
    left: 69,
    top: 9,
  },
  {
    name: "Restaurant",
    attributes: ["id: String", "name: String", "address: String"],
    relations: ["bookings: Booking[]"],
    left: 37.5,
    top: 41,
  },
];

const DURATIONS = [900, 900, 1200, 900, 1200, 3200];

// Cursor positions (in %) for each step
const CURSOR = [
  { left: 24, top: 28 },
  { left: 84, top: 26 },
  { left: 52, top: 24 },
  { left: 56, top: 60 },
  { left: 75, top: 50 },
  { left: 46, top: 36 },
];

/**
 * Step 3: a whiteboard where the entities of the future app take shape.
 */
export function BoardVisual({ active }: { active: boolean }) {
  const t = useTranslations("Home.process.visuals.board");
  const step = useSequence(active, DURATIONS);

  const visibleEntities = [step >= 0, step >= 1, step >= 3];
  const drawFirstLink = step >= 2;
  const drawSecondLink = step >= 4;

  return (
    <AppWindow>
      <div className="relative size-full bg-[#f4f4f5] bg-[radial-gradient(#cfcfd4_0.11cqw,transparent_0.12cqw)] bg-[length:2.2cqw_2.2cqw]">
        {/* Top-right toolbar */}
        <div className="absolute top-[1.2cqw] right-[1.2cqw] flex items-center gap-[1cqw] rounded-[1cqw] bg-white px-[0.9cqw] py-[0.6cqw] shadow-sm ring-1 ring-black/5">
          <span className="flex items-center gap-[0.3cqw]">
            <Avatar
              name="Bryan Cellier"
              colorIndex={4}
              className="size-[2cqw] text-[0.75cqw]"
            />
            <ChevronDown className="size-[1cqw] text-black/50" />
          </span>
          <Sparkles className="size-[1.4cqw] text-black/70" />
          <LayoutPanelTop className="size-[1.4cqw] text-black/70" />
          <MessageCircle className="size-[1.4cqw] text-black/70" />
          <span className="rounded-[0.6cqw] bg-[#9747ff] px-[1.1cqw] py-[0.45cqw] text-[1cqw] font-medium text-white">
            {t("share")}
          </span>
        </div>

        {/* Links */}
        <svg
          viewBox="0 0 100 56.25"
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 size-full"
          aria-hidden
        >
          <motion.path
            d="M28.5 16 H69"
            fill="none"
            stroke="#9a9aa2"
            strokeWidth="0.18"
            initial={false}
            animate={{ pathLength: drawFirstLink ? 1 : 0 }}
            transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
          />
          <motion.path
            d="M60 31 H80 V29.2"
            fill="none"
            stroke="#9a9aa2"
            strokeWidth="0.18"
            initial={false}
            animate={{ pathLength: drawSecondLink ? 1 : 0 }}
            transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
          />
        </svg>
        <AnimatePresence>
          {drawFirstLink && (
            <motion.span
              key="link-1"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute top-[24.5%] left-[48.7%] -translate-x-1/2 rounded-[0.4cqw] bg-[#f4f4f5] px-[0.5cqw] text-[0.9cqw] text-black/70"
            >
              {t("relation")}
            </motion.span>
          )}
          {drawSecondLink && (
            <motion.span
              key="link-2"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.5 }}
              className="absolute top-[52%] left-[70%] -translate-x-1/2 rounded-[0.4cqw] bg-[#f4f4f5] px-[0.5cqw] text-[0.9cqw] text-black/70"
            >
              {t("relation")}
            </motion.span>
          )}
        </AnimatePresence>

        {/* Entities */}
        <AnimatePresence>
          {ENTITIES.map((entity, index) =>
            visibleEntities[index] ? (
              <motion.div
                key={entity.name}
                initial={{ opacity: 0, scale: 0.85, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 320, damping: 24 }}
                className="absolute w-[23%] overflow-hidden rounded-[0.7cqw] bg-white shadow-[0_0.6cqw_1.6cqw_rgb(0_0_0/0.08)] ring-1 ring-black/[0.07]"
                style={{ left: `${entity.left}%`, top: `${entity.top}%` }}
              >
                <p className="bg-[#0d99ff] py-[0.65cqw] text-center text-[1.15cqw] font-medium text-white">
                  {entity.name}
                </p>
                <div className="px-[0.9cqw] py-[0.8cqw] font-mono text-[0.82cqw] leading-[1.45] text-black/80">
                  <p className="mb-[0.2cqw] font-sans text-[0.95cqw] font-semibold text-black">
                    {t("attributes")}
                  </p>
                  {entity.attributes.map((attribute) => (
                    <p key={attribute}>- {attribute}</p>
                  ))}
                  <p className="mt-[0.5cqw] mb-[0.2cqw] font-sans text-[0.95cqw] font-semibold text-black">
                    {t("relations")}
                  </p>
                  {entity.relations.map((relation) => (
                    <p key={relation}>- {relation}</p>
                  ))}
                </div>
              </motion.div>
            ) : null
          )}
        </AnimatePresence>

        {/* Bottom toolbar */}
        <div className="absolute bottom-[1.4cqw] left-1/2 flex -translate-x-1/2 items-stretch overflow-hidden rounded-[1cqw] bg-white shadow-sm ring-1 ring-black/[0.08]">
          <div className="flex flex-col border-r border-black/[0.07]">
            <span className="grid flex-1 place-items-center px-[0.7cqw]">
              <MousePointer2 className="size-[1.2cqw]" />
            </span>
            <span className="grid flex-1 place-items-center bg-[#9747ff] px-[0.7cqw] text-white">
              <Hand className="size-[1.2cqw]" />
            </span>
          </div>
          <div className="flex items-end px-[1.4cqw] pt-[0.8cqw]">
            <span className="block h-[3.2cqw] w-[0.9cqw] bg-gradient-to-b from-[#1e1e1e] from-30% to-[#e5e5e5] to-30% [clip-path:polygon(50%_0,100%_30%,100%_100%,0_100%,0_30%)]" />
          </div>
          <div className="relative flex items-center gap-[0.5cqw] bg-[#f3e8ff] px-[1cqw] py-[0.8cqw]">
            <span className="absolute -top-[0.1cqw] left-1/2 -translate-x-1/2 text-[0.75cqw] whitespace-nowrap text-[#9747ff]">
              {t("newShapes")}
            </span>
            <span className="mt-[0.9cqw] size-[1.6cqw] rounded-full bg-[#d8b4fe]" />
            <span className="mt-[0.9cqw] size-[1.5cqw] rounded-[0.2cqw] bg-[#d8b4fe]" />
            <CornerUpRight className="mt-[0.9cqw] size-[1.3cqw] text-[#c084fc]" />
          </div>
          <div className="flex items-center gap-[0.6cqw] border-l border-black/[0.07] px-[1cqw]">
            {[Type, Frame, Table, Stamp, Plus].map((Icon, index) => (
              <span
                key={index}
                className="grid size-[2.4cqw] place-items-center rounded-[0.5cqw] ring-1 ring-black/10"
              >
                <Icon className="size-[1.3cqw]" />
              </span>
            ))}
          </div>
        </div>

        {/* Collaborator cursor */}
        <motion.div
          className="absolute"
          initial={false}
          animate={{
            left: `${CURSOR[step].left}%`,
            top: `${CURSOR[step].top}%`,
          }}
          transition={{ duration: 0.9, ease: [0.45, 0, 0.2, 1] }}
        >
          <Cursor label="Bryan" color="#9747ff" />
        </motion.div>
      </div>
    </AppWindow>
  );
}

// =============================================================================
